// React & Hooks
import { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// Helpers
import { classNames, useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";
import { getActiveUser } from "services/api/users";
import { getPrimaryIban } from "services/api/tenants/consts";
import { getSettings } from "services/api/settings/redux";

// Validation & Types
import { Errors } from "services/forms/validation/types";
import { FLEET_VEHICLE_CONTRACT_RENT_EDIT_FORM_SCHEMA } from "./validation";
import { VehicleContractRentEditProps } from "./types";
import {
  FleetVehicleContractRent,
  FleetVehicleContractRentEditFormValues,
  ContractRentStatusHistory,
} from "services/api/fleet/vehicles/contracts/types";
import { initialFleetVehicleContractRentEditForm, ContractRentStatusId } from "services/api/fleet/vehicles/contracts/consts";

// API
import {
  createFleetVehicleContractRentAPI,
  getContractRentGenerateNumberAPI,
  getFleetVehicleContractRentAPI,
  getContractRentIsAvailableAPI,
  getFleetVehicleContractRentPdfPreviewAPI,
  toggleContractRentStatusHistoryAPI,
  updateFleetVehicleContractRentAPI,
} from "services/api/fleet/vehicles/contracts/api";
import ContractEmailModal from "./components/ContractEmailModal/ContractEmailModal";


// UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";
import { PdfViewer } from "ui/components/PdfViewer";
import VehicleContractContent from "./components/VehicleContractContent/VehicleContractRentContent";
import { Tabs } from "ui/components/Tabs";
import VehicleContractInfo from "./components/VehicleContractInfo/VehicleContractRentInfo";
import { transformFleetVehicleContractRentEditForm } from "services/api/fleet/vehicles/contracts/transformations";
import ContractStatusToolbar from "./components/ContractStatusToolbar";
import FeatureUnavailable from "ui/components/Availability/FeatureUnavailable/FeatureUnavailable";
import { useAvailability } from "services/api/hooks/useAvailability";
import { Modal, SuccessOverlay } from "ui/components/Modal";
import DocumentStatusIntro from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/DocumentStatusIntro/DocumentStatusIntro";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import successAnimation from "ui/theme/animations/success.json";
import {
  FLEET_VEHICLE_CONTRACT_RENT_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_CONTRACT_RENT_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";


const VehicleContractRentEditCard: React.FC<VehicleContractRentEditProps> = (props) => {
  const { show, vehicleId, contractId, offerRentId, onSuccess, onClose, onCreateInvoice } = props;

  const { t } = useTranslate();
  const { user } = useSelector(getActiveUser);
  const settings = useSelector(getSettings);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  // Only show intro animation for new contracts (no contractId)
  const [showIntro, setShowIntro] = useState<boolean>(!contractId);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [createdContractId, setCreatedContractId] = useState<string | null>(null);
  const [statusHistory, setStatusHistory] = useState<ContractRentStatusHistory[]>([]);
  const [currentStatusId, setCurrentStatusId] = useState<string | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
  const [hasAvailableOffers, setHasAvailableOffers] = useState<boolean>(true);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);

  // Preduvjeti uključuju i stanje vozila (prodano vozilo ne može dobiti novi ugovor).
  const checkAvailability = useCallback(
    () => getContractRentIsAvailableAPI(vehicleId),
    [vehicleId]
  );

  const { isAvailable: isSettingsAvailable, renderUnavailable } = useAvailability({
    apiFn: checkAvailability,
    enabled: show && !contractId,
  });
  const [formValues, setFormValues] =
    useState<FleetVehicleContractRentEditFormValues>(
      initialFleetVehicleContractRentEditForm
    );

  // Ugovor se može otvoriti i izvan vozila (globalna pretraga ugovora) — tada vozilo stiže
  // s odabrane ponude, pa se bez ovoga ne bi ni prikazalo ni spremilo.
  const effectiveVehicleId = vehicleId || formValues.vehicleId || "";

  useEffect(() => {
    if (!show) return;
    let active = true;

    if (contractId) {
      fetchContract(contractId, () => active);
    } else {
      fetchNewContractNumber();
      setShowIntro(true);
    }

    return () => { active = false; };
  }, [show, contractId]);

  useEffect(() => {
    if (!vehicleId) return;

    setFormValues(prev => ({
      ...prev,
      vehicleId,
      offerRentId: offerRentId ?? prev.offerRentId,
    }));
  }, [vehicleId]);

  useEffect(() => {
    // Auto-fill lessor fields from tenant data for new contracts
    if (!contractId && user?.tenant) {
      const tenant = user.tenant;
      setFormValues(prev => ({
        ...prev,
        lessorCompanyName: tenant.companyName || null,
        lessorHeadquartersAddress: tenant.headquartersAddress || null,
        lessorPersonalIdentificationNumber: tenant.personalIdentificationNumber || null,
        lessorPhoneNumber: tenant.phoneNumber || null,
        lessorTransactionAccountNumber: getPrimaryIban(tenant),
        lessorSalesPersonName: `${user.firstName} ${user.lastName}`,
      }));
    }
  }, [contractId, user?.tenant]);

  // Auto-fill delivery defaults from settings for new contracts
  useEffect(() => {
    if (contractId) return;

    setFormValues(prev => ({
      ...prev,
      deliveryTime: prev.deliveryTime ?? settings?.contractSaleDeliveryTime ?? null,
      placeOfVehicleDelivery: prev.placeOfVehicleDelivery ?? settings?.contractSalePlaceOfVehicleDelivery ?? null,
    }));
  }, [contractId, settings?.contractSaleDeliveryTime, settings?.contractSalePlaceOfVehicleDelivery]);

  const fetchContract = async (id: string, isActive: () => boolean = () => true) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleContractRentEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleContractRentAPI(id);
    } catch {
      if (isActive()) setIsLoading(false);
      return;
    }

    if (!isActive()) return;

    setFormValues(transformFleetVehicleContractRentEditForm(result));
    setStatusHistory(result.statusHistory ?? []);
    setCurrentStatusId(result.status?.id ?? null);
    setErrors({});

    // Load PDF preview before hiding loader so everything appears at once
    const url = await getFleetVehicleContractRentPdfPreviewAPI(id);
    if (!isActive()) return;

    if (url) {
      setPdfUrl(url);
      setShowPdf(true);
    }

    setIsLoading(false);
  };

  const fetchNewContractNumber = async () => {
    setIsLoading(true);

    try {
      let newContractNumber = await getContractRentGenerateNumberAPI();
      if (newContractNumber) {
        setFormValues(prev => ({
          ...prev,
          contractNumber: newContractNumber,
        }));
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setErrors({});
  };

  const handleOfferRentsLoaded = (count: number) => {
    setIsLoading(false);
    setHasAvailableOffers(count > 0);
  };

  // Check if Info tab has errors
  const hasInfoTabErrors = (errors: Errors): boolean => {
    // Get all validation fields except content tab fields
    const contentTabFields = ['contractRentTemplateId'];
    const allValidationFields = Object.keys(FLEET_VEHICLE_CONTRACT_RENT_EDIT_FORM_SCHEMA.fields);
    const infoTabFields = allValidationFields.filter(field => !contentTabFields.includes(field));

    return infoTabFields.some(field => errors[field]);
  };

  // Check if Content tab has errors
  const hasContentTabErrors = (errors: Errors): boolean => {
    const contentTabFields = [
      'contractRentTemplateId'
    ];
    return contentTabFields.some(field => errors[field]);
  };

  const validateForm = (): boolean => {
    let validationErrors: Errors = {};
    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_CONTRACT_RENT_EDIT_FORM_SCHEMA,
      (errs) => { validationErrors = errs; setErrors(errs); }
    );

    if (!isValid) {
      if (hasInfoTabErrors(validationErrors)) {
        setActiveTab(0);
      } else if (hasContentTabErrors(validationErrors)) {
        setActiveTab(1);
      }
    }

    return isValid;
  };

  // Save as draft or update existing contract
  const handleSaveDraft = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let result: FleetVehicleContractRent | undefined;

      if (formValues.id) {
        result = await updateFleetVehicleContractRentAPI(
          formValues.id,
          effectiveVehicleId,
          formValues,
          t
        );
        if (result) {
          setStatusHistory(result.statusHistory ?? []);
          setCurrentStatusId(result.status?.id ?? null);
        }
      } else {
        const draftFormValues = {
          ...formValues,
          contractRentStatusId: ContractRentStatusId.Draft,
        };
        result = await createFleetVehicleContractRentAPI(effectiveVehicleId, draftFormValues, t);
        if (result) {
          setFormValues(prev => ({ ...prev, id: result!.id, contractRentStatusId: result!.status?.id ?? ContractRentStatusId.Draft }));
          setCreatedContractId(result!.id);
          setStatusHistory(result.statusHistory ?? []);
          setCurrentStatusId(result.status?.id ?? null);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  // Create contract from draft (change status to Created)
  const handleCreateContractFromDraft = async (e: any) => {
    e.preventDefault();

    if (!formValues.id) return;
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const createdFormValues = { ...formValues, contractRentStatusId: ContractRentStatusId.Created };
      const result = await updateFleetVehicleContractRentAPI(
        formValues.id,
        vehicleId,
        createdFormValues,
        t
      );
      if (result) {
        setFormValues(prev => ({ ...prev, contractRentStatusId: ContractRentStatusId.Created }));
        setStatusHistory(result.statusHistory ?? []);
        setCurrentStatusId(result.status?.id ?? null);
        setShowPdf(false);
        setShowSuccessOverlay(true);
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handlePreviewPdf = async (e: any) => {
    e.preventDefault();

    // Predložak spremljen kao draft nema `contractId` prop, ali ima `formValues.id`/`createdContractId`.
    const id = contractId || createdContractId || formValues.id;
    if (!id) return;
    const url = await getFleetVehicleContractRentPdfPreviewAPI(id);
    if (url) {
      setPdfUrl(url);
      setShowPdf(true);
    }
  };

  const handlePrintPdf = async (e: any) => {
    e.preventDefault();

    const id = contractId || createdContractId || formValues.id;
    if (!id) return;
    const url = await getFleetVehicleContractRentPdfPreviewAPI(id);
    if (url) {
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      }
    }
  };

  const handleSuccessDismiss = async () => {
    setShowSuccessOverlay(false);

    if (createdContractId) {
      const url = await getFleetVehicleContractRentPdfPreviewAPI(createdContractId);
      if (url) {
        setPdfUrl(url);
        setShowPdf(true);
      }
    }
  };

  const handleStatusToggle = async (statusId: string, checked: boolean) => {
    const id = contractId || createdContractId;
    if (!id) return;

    try {
      const result = await toggleContractRentStatusHistoryAPI(id, statusId, checked);
      setStatusHistory(result.statusHistory ?? []);
      setCurrentStatusId(result.status?.id ?? null);
    } catch {
      // error handled by snackbar
    }
  };

  const handleSendEmail = (e: any) => {
    e.preventDefault();
    setShowEmailModal(true);
  };

  const handleEmailModalHide = () => {
    setShowEmailModal(false);
  };

  // Nakon slanja backend označava korak "Poslan" — traka statusa se dohvaća iznova
  const handleEmailSent = async () => {
    const id = contractId || createdContractId;
    if (!id) return;

    try {
      const result = await getFleetVehicleContractRentAPI(id);
      setStatusHistory(result.statusHistory ?? []);
      setCurrentStatusId(result.status?.id ?? null);
      setFormValues(prev => ({ ...prev, contractRentStatusId: result.status?.id ?? prev.contractRentStatusId }));
    } catch {
      // greška se prikazuje putem snackbara
    }
  };

  const handleCancelContract = (e: any) => {
    e.preventDefault();
    setOpenCancelDialog(true);
  };

  const handleCancelDialogConfirm = async () => {
    setOpenCancelDialog(false);
    if (!formValues.id) return;

    try {
      const cancelledFormValues = { ...formValues, contractRentStatusId: ContractRentStatusId.Cancelled };
      const result = await updateFleetVehicleContractRentAPI(
        formValues.id,
        vehicleId,
        cancelledFormValues,
        t
      );
      if (result) {
        setFormValues(prev => ({ ...prev, contractRentStatusId: ContractRentStatusId.Cancelled }));
        setStatusHistory(result.statusHistory ?? []);
        setCurrentStatusId(result.status?.id ?? null);
      }
    } catch {
      // error handled by snackbar
    }
  };

  const isCancelled = currentStatusId === ContractRentStatusId.Cancelled || formValues.contractRentStatusId === ContractRentStatusId.Cancelled;

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        position="side"
        size="medium"
        intro={showIntro ? {
          content: (
            <DocumentStatusIntro
              documentType="contract"
              isRent
              statusId={currentStatusId}
            />
          ),
        } : undefined}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={
            contractId
              ? FLEET_VEHICLE_CONTRACT_RENT_EDIT_PAGE_TITLE.text
              : FLEET_VEHICLE_CONTRACT_RENT_CREATE_PAGE_TITLE.text
          }
          onHide={onClose}
        />
        <ModalBody>
          {isLoading && (
            <div className="relative h-[calc(100vh-80px)]">
              <Loader isLoading={true}>{null}</Loader>
            </div>
          )}
          <form noValidate className={isLoading ? "hidden" : ""}>
            {renderUnavailable({ title: "Ugovor nije dostupan", onClose })}
            <FeatureUnavailable
              show={isSettingsAvailable && !hasAvailableOffers && !contractId && !isLoading}
              title="Nema dostupnih ponuda"
              description="Za kreiranje ugovora potrebno je imati barem jednu aktivnu ponudu koja nije vezana na postojeći ugovor. Molimo kreirajte prvo ponudu."
              onClose={onClose}
            />
            <div className={(!isSettingsAvailable || !hasAvailableOffers) && !contractId ? "hidden" : ""}>
            <div className="px-4 mt-6">
              <ContractStatusToolbar
                statusHistory={statusHistory}
                onToggle={handleStatusToggle}
                disabled={isCancelled}
              />
            </div>
            {isCancelled && (
              <div className="bg-red-50 border border-red-200 rounded p-4 mb-4 mx-4">
                <p className="text-red-800 font-medium text-center">Ugovor je storniran</p>
              </div>
            )}
            <div className={isCancelled ? "opacity-50 pointer-events-none flex mt-4 mb-6 px-4 min-h-[70vh] 2xl:min-h-[1000px]" : "flex mt-4 mb-6 px-4 min-h-[70vh] 2xl:min-h-[1000px]"}>

              <Tabs
                activeIndex={activeTab}
                onTabChange={setActiveTab}
                tabs={[
                  {
                    label: t("admin.fleet.vehicle.contract.add.tab.info"),
                    hasError: hasInfoTabErrors(errors),
                    content: <div className="px-4 py-3 border border-slate-200 -mt-[1px]">
                      <VehicleContractInfo
                        vehicleId={effectiveVehicleId}
                        formValues={formValues}
                        setFormValues={setFormValues}
                        errors={errors}
                        setErrors={setErrors}
                        onOfferRentsLoaded={handleOfferRentsLoaded} />
                    </div>,
                  },
                  {
                    label: t("admin.fleet.vehicle.contract.add.tab.content"),
                    hasError: hasContentTabErrors(errors),
                    content: <div className="px-4 py-3 border border-slate-200 -mt-[1px]">
                      <VehicleContractContent
                        formValues={formValues}
                        setFormValues={setFormValues}
                        errors={errors}
                        setErrors={setErrors} />
                    </div>,
                  },
                ]}
              />
            </div>
            {!isCancelled && (
              <div className={classNames(
                "px-4 py-3 fixed bottom-0 bg-slate-50 border border-t-slate-200 flex justify-between gap-3",
                showIntro ? "right-0 w-1/2" : "left-0 w-full"
              )}>
                <div className="flex gap-2">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={() => {
                      if (createdContractId && onSuccess) {
                        onSuccess();
                      } else {
                        onClose();
                      }
                    }}
                  />
                  {formValues.id && formValues.contractRentStatusId !== ContractRentStatusId.Draft && (
                    <Button
                      variant="remove"
                      children="Storniraj ugovor"
                      onClick={handleCancelContract}
                    />
                  )}
                  {formValues.id && !showPdf && (
                    <Button
                      startIcon="/media/icons/search.svg"
                      variant="secondary"
                      children={t("admin.preview.pdf")}
                      onClick={handlePreviewPdf}
                    />
                  )}
                  {formValues.id && showPdf && (
                    <Button
                      startIcon="/media/icons/print.svg"
                      variant="secondary"
                      children="Ispiši ugovor"
                      onClick={handlePrintPdf}
                    />
                  )}
                  {/* Slanje najmoprimcu — predložak se ne šalje */}
                  {formValues.id && formValues.contractRentStatusId !== ContractRentStatusId.Draft && (
                    <Button
                      startIcon="/media/icons/email.svg"
                      variant="secondary"
                      children="Pošalji ugovor"
                      onClick={handleSendEmail}
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  {formValues.id && formValues.contractRentStatusId !== ContractRentStatusId.Draft && onCreateInvoice && (
                    <Button
                      variant="secondary"
                      children="Kreiraj račun"
                      onClick={() => onCreateInvoice(formValues.id!)}
                    />
                  )}
                  <Button
                    variant="primary"
                    children={
                      !formValues.id
                        ? "Spremi predložak"
                        : formValues.contractRentStatusId === ContractRentStatusId.Draft
                          ? "Kreiraj ugovor"
                          : "Spremi ugovor"
                    }
                    onClick={
                      formValues.id && formValues.contractRentStatusId === ContractRentStatusId.Draft
                        ? handleCreateContractFromDraft
                        : handleSaveDraft
                    }
                  />
                </div>
              </div>
            )}

            {pdfUrl && (
              <PdfViewer
                url={pdfUrl}
                show={showPdf}
                onRefresh={async () => {
                  const id = contractId || createdContractId;
                  if (!id) return;
                  const url = await getFleetVehicleContractRentPdfPreviewAPI(id);
                  if (url) setPdfUrl(url);
                }}
              />
            )}
            </div>
          </form>
        </ModalBody>
      </Modal>
      {/* Slanje ugovora e-mailom */}
      <ContractEmailModal
        show={showEmailModal}
        onHide={handleEmailModalHide}
        formValues={formValues}
        onSent={handleEmailSent}
      />
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Ugovor je uspješno kreiran!"
        description="Možete ga pregledati ili poslati klijentu"
        onDismiss={handleSuccessDismiss}
      />
      <Dialog
        open={openCancelDialog}
        title={t("admin.fleet.vehicle.contract.rent.dialog.cancel.title")}
        message={t("admin.fleet.vehicle.contract.rent.dialog.cancel.message")}
        confirmText={t("admin.fleet.vehicle.contract.rent.dialog.cancel.confirmtext")}
        cancelText={t("admin.fleet.vehicle.contract.rent.dialog.cancel.canceltext")}
        onConfirm={handleCancelDialogConfirm}
        onCancel={() => setOpenCancelDialog(false)}
      />
    </>
  );
};
export default memo(VehicleContractRentEditCard);
